import React, { useState } from "react";
import { Form, Input, Button, Flex } from "antd";
import { useLocation, useNavigate } from "react-router-dom";
import {
  MailFilled,
  LockFilled,
  CheckCircleFilled,
  CloseCircleFilled,
  InfoCircleOutlined,
} from "@ant-design/icons";
import { TResponseModel, TUser } from "../../types/config";
import EncryptUtils from "../../utils/encrypt";
import useFetch from "../../hooks/useFetch";
import { ApiSignin, GetMenuHierarchyList } from "../../utils/api.constant";
import { eLoginMode, eResultCode } from "../../utils/enum";
import useToast from "../../hooks/useToast";
import AuthUtil from "../../utils/auth";
import { ToastOpen, ToastType } from "../../state/toast/slice";
import Utils from "../../utils";
import styles from "./auth.module.css";

const Login: React.FC = () => {
  const navigate = useNavigate();
  const { post } = useFetch();
  const { onShowToast } = useToast();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [form] = Form.useForm();
  const emailId = Form.useWatch("emailId", form);

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  const loginFormEmail = atob(queryParams.get("e") || "");

  const onFinish = async (values: TUser) => {
    try {
      setIsLoading(true);
      const password = EncryptUtils.encrypt(values.password as string);
      // Prepare payload for API request
      const payload = {
        userName: values.emailId,
        password: password,
        loginMode: eLoginMode.PASSWORD,
      };

      // Make API request for sign-in
      const response: TResponseModel = await post(ApiSignin, payload);

      // Handle API response
      const { dataResponse, authToken } = response;
      const { returnCode, description } = dataResponse;

      if (returnCode === eResultCode.SUCCESS) {
        AuthUtil.setToken(authToken as string);
        AuthUtil.activeLoggedUserService();
        // Display success toast
        onShowToast({
          type: ToastType.success,
          title: <CheckCircleFilled />,
          position: ToastOpen.leftBottom,
          content: description,
        });
        setIsLoading(false);
        // Redirect to dashboard after a brief delay
        setTimeout(async () => {
          const menus = await getMenuHierarchyList();
          Utils.redirectUrl(menus?.[0]?.entityUrl);
        }, 700);
      } else if (returnCode === eResultCode.SERVICE_ERROR) {
        setIsLoading(false);
        onShowToast({
          time: 9000,
          type: ToastType.info,
          title: <InfoCircleOutlined />,
          position: ToastOpen.leftBottom,
          content: dataResponse.description,
        });
        onClickForgotPassword("t");
      } else {
        // Display error toast
        setIsLoading(false);
        onShowToast({
          type: ToastType.error,
          title: <CloseCircleFilled />,
          position: ToastOpen.leftBottom,
          content: dataResponse.description,
        });
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Function to fetch menu hierarchy list from the server
  const getMenuHierarchyList = async () => {
    try {
      const requestPayload = {
        data: {
          renderMenuRoleWise: true,
        },
      };
      const response = await post(GetMenuHierarchyList, requestPayload);
      const { data, dataResponse } = response;
      const { returnCode, description } = dataResponse;
      if (returnCode === eResultCode.SUCCESS) {
        return data;
      } else {
        console.error(`Error while fetching menu hierarchy: ${description}`);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const onClickForgotPassword = (at: string = "f") => {
    navigate(`/forgot-password?e=${emailId ? btoa(emailId) : ""}&at=${at}`);
  };

  return (
    <div className={styles.parentWrapper}>
      <Form
        form={form}
        name="login"
        initialValues={{ emailId: loginFormEmail }}
        onFinish={onFinish}
        autoComplete="off"
        layout="vertical"
      >
        <div className={styles.formHeader}>
          <div style={{ display: "flex", marginTop: "1rem" }}>
            <span className={styles.greetSession}>Login </span>
            <h2 style={{ paddingLeft: "5px" }}> Your Account</h2>
          </div>
        </div>
        <Form.Item
          name="emailId"
          label="Email address"
          rules={[
            { required: true, message: "Please enter your Email address" },
          ]}
        >
          <Input
            autoComplete="off"
            prefix={<MailFilled />}
            placeholder="Your email address"
            size="large"
          />
        </Form.Item>
        <Form.Item
          name="password"
          label="Password"
          rules={[{ required: true, message: "Please enter your Password" }]}
        >
          <Input.Password
            autoComplete="off"
            prefix={<LockFilled />}
            type="password"
            placeholder="Password"
            size="large"
          />
        </Form.Item>
        <Form.Item>
          <Flex justify="space-between" align="center">
            <Button type="link" onClick={() => onClickForgotPassword()}>
              Forgot Password
            </Button>
          </Flex>
        </Form.Item>

        <Form.Item>
          <Button
            block
            type="primary"
            htmlType="submit"
            disabled={isLoading}
            loading={isLoading}
            size="large"
          >
            Sign in
          </Button>
        </Form.Item>
      </Form>
    </div>
  );
};

export default Login;
